import { defineStore } from "pinia";
import axios from "axios";
import {
  ApplicationSummary,
  ApplicationComment,
  ApplicationDetailVM,
  ApplicationStatuses,
  TabVM,
  TabApplicationVM,
} from "@/types/application";
import { $api } from "@/services/api1";
import { BaseOption, LOSType, ProductIds } from "@/types/types";
import functions from "@/use/functions";
import type {
  AddressDictionary,
  ApplicantAddress,
  ApplicantVM,
  ApplicantFormVM,
} from "@/types/applicant";
import type { StudentFormVM, StudentVM } from "@/types/student";
import useFunctions from "@/use/functions1";
import { Tag } from "@/types/tag";
import { ApplicationStatusIds } from "@/types/application";
import { AuthorizedThirdPartyVM } from "@/types/authorized-third-party";
import { TabResponse } from "@/types/tabs";

const { decodeHTMLEncodedStr } = useFunctions();
interface Application {
  detailViewModel: ApplicationDetailVM;
  tabApplicationViewModel: TabApplicationVM;
}

interface SchoolUpdate {
  schoolId?: number;
  schoolName?: string;
  highestDegreeLevel?: string;
  academicProgramId?: number | null;
  academicProgramName?: string | null;
  degreeProgram?: string; // legacy
}

export const useApplicationStore = defineStore("applicationVM", {
  state: () => {
    return {
      fallBackApplicationId: 0 as number,
      applicationSummary: null as ApplicationSummary | null,
      application: null as Application | null,
      applicationComments: [] as ApplicationComment[],
      viewDisabled: false,
      applicantPreviousAddresses: {} as AddressDictionary,
      applicants: [] as ApplicantVM[],
      students: [] as StudentVM[],
      applicantsTab: [] as ApplicantFormVM[],
      studentsTab: [] as StudentFormVM[],
      applicationMetadata: {
        referenceId: null as string | null,
        applicationId: null as number | null,
        lenderId: null as number | null,
        productTypeId: null as number | null,
        programTypeId: null as number | null,
      },
      authorizedThirdPartyForm: {
        id: 0,
        firstName: "",
        lastName: "",
        initials: "",
        fullName: "",
        emailAddress: "",
        dateOfBirth: "",
        address: {
          id: 0,
          addressId: 0,
          applicantId: 0,
          address1: "",
          address2: "",
          addressTypeId: 0,
          city: "",
          stateId: 0,
          stateName: "",
          postalCode: "",
          countryId: 0,
          countryName: "",
        },
        phoneNumber: "",
        phoneNumberTypeId: 0,
        phoneNumberTypeName: "",
        authorizedByApplicantId: 0,
        selected: true,
        sourceTypeId: 1,
      } as AuthorizedThirdPartyVM,
      tabs: [] as TabVM[],
      tags: [] as Tag[],
    };
  },

  getters: {
    viewModel(state): ApplicationDetailVM {
      return state.application?.detailViewModel || ({} as ApplicationDetailVM);
    },
    applicationTab(state): TabApplicationVM {
      return (
        state.application?.tabApplicationViewModel || ({} as TabApplicationVM)
      );
    },
    summary(state): ApplicationSummary {
      return state.applicationSummary || ({} as ApplicationSummary);
    },
    applicationStatuses(state): LOSType[] {
      return (
        state.applicationSummary?.applicationStatuses.filter(
          (a) => a.visible
        ) || []
      );
    },
    pageIdentifier(state): string {
      return state.application?.detailViewModel?.pageIdentifier || "";
    },
    applicationTabs(state): TabVM[] {
      return state.tabs || [];
    },
    applicationTags(state): Tag[] {
      return state.tags || [];
    },
    applicationStatus(state): string {
      return state.application?.detailViewModel?.statusName || "";
    },
    applicationStatusId(state): number {
      return state.application?.detailViewModel?.statusId || 0;
    },
    productTypeId(state): number {
      return state.application?.detailViewModel?.productTypeId || 0;
    },
    applicationId(state): number {
      return (
        state.application?.detailViewModel?.id || state.fallBackApplicationId
      );
    },
		currentTabIdentifier(state): string {
			return state.tabs?.filter(t => t.isTabActive)[0]?.identifier || ""
		},
    referenceId(state): string {
      return state.application?.detailViewModel.referenceId || "";
    },
    Applicants(state): ApplicantVM[] {
      return state.applicants;
    },
    Students(state): StudentVM[] {
      return state.students;
    },
    ApplicantSelected(state): ApplicantFormVM {
      return (
        state.applicantsTab.find((a) => a.selected) || ({} as ApplicantFormVM)
      );
    },
    StudentSelected(state): StudentFormVM {
      return state.studentsTab.find((a) => a.selected) || ({} as StudentFormVM);
    },
    AuthorizedSelected(state): AuthorizedThirdPartyVM {
      return (
        state.application?.tabApplicationViewModel.authorizedThirdParty?.find(
          (a) => a.selected
        ) || state.authorizedThirdPartyForm
      );
    },
    AuthorizedParties(state): AuthorizedThirdPartyVM[] {
      return (
        state.application?.tabApplicationViewModel.authorizedThirdParty || []
      );
    },
    applicantIds(state): number[] {
      const response = Array<number>();
      if (state.application?.detailViewModel.hasPrimaryApplicant)
        response.push(state.application?.detailViewModel.primaryApplicant.id);
      if (state.application?.detailViewModel.hasCoApplicant)
        response.push(state.application?.detailViewModel.coApplicant.id);
      return response;
    },
    comments(state): any {
      return state.applicationComments ?? [];
    },
    applicantOptions(state): BaseOption[] {
      const applicantOption: BaseOption[] = [];
      if (state.application?.detailViewModel.hasPrimaryApplicant) {
        applicantOption.push({
          value: state.application?.detailViewModel.primaryApplicant.id,
          label: `${state.application?.detailViewModel.primaryApplicant.fullName} - ${state.application?.detailViewModel.primaryApplicant.applicantTypeName}`,
        });
      }

      if (state.application?.detailViewModel.hasCoApplicant) {
        applicantOption.push({
          value: state.application?.detailViewModel.coApplicant.id,
          label: `${state.application?.detailViewModel.coApplicant.fullName} - ${state.application?.detailViewModel.coApplicant.applicantTypeName}`,
        });
      }
      return applicantOption;
    },
    applicantUserId: (state) => {
      return (applicantId: number) => {
        return state.applicants.find((a) => a.id == applicantId)?.userId;
      };
    },
    applicantFullName: (state) => {
      return (applicantId: number) => {
        if (
          state.application?.detailViewModel.primaryApplicant.id == applicantId
        ) {
          return state.application?.detailViewModel.primaryApplicant.fullName;
        }

        if (state.application?.detailViewModel.coApplicant.id == applicantId) {
          return state.application?.detailViewModel.coApplicant.fullName;
        }

        return "";
      };
    },
    applicantAddresses: (state) => {
      return (applicantId: number) => {
        return state.applicantPreviousAddresses[applicantId] || [];
      };
    },
    canGenerateLegalPacket: (state): boolean => {
      return (
        state.application?.detailViewModel?.statusId ===
        ApplicationStatuses.SubmittedProcessing
      );
    },
    canAccessByStatuses: (state) => {
      return (statusIds: number[]) => {
        return statusIds.some(
          (id) => id == state.application?.detailViewModel.statusId
        );
      };
    },
    overridesTabEditable: (state: any): boolean => {
      return [
        ApplicationStatusIds.SUBMITTED_AND_PROCESSING,
        ApplicationStatusIds.DECLINED,
        ApplicationStatusIds.CANCELLED,
      ].includes(state.application?.detailViewModel?.statusId);
    },
    addAuthorizedThirdPartyAllowed: (state: any): boolean => {
      const allowedStatuses = [
        ApplicationStatusIds.NOT_SUBMITTED,
        ApplicationStatusIds.EVALUATION,
        ApplicationStatusIds.SUBMITTED_AND_PROCESSING,
        ApplicationStatusIds.RIGHT_TO_CANCEL,
        ApplicationStatusIds.SCHOOL_CERTIFICATION,
      ];

      return allowedStatuses.includes(
        state.application?.detailViewModel?.statusId
      );
    },
    isFloorDisabled: (state: any): boolean => {
      return state.application?.detailViewModel.productTypeId === ProductIds.PEL
        ? true
        : false;
    },
    isParentLOC: (state: any): boolean => {
      return state.application?.detailViewModel?.isParentLOC;
    },
  },
  actions: {
    async getTabTags(referenceId: string) {
      const tabs: TabResponse = await $api.applications.getTabTagsAsync(
        referenceId
      );

      const currentTab = this.tabs?.find((t) => t.isTabActive);

      if (tabs.success) {
        this.tabs = tabs.results.tabs;
        this.tags = tabs.results.tags;

        if (currentTab) {
          this.updateActiveTab(currentTab.identifier);
        }
      }
    },
    async updateTabTags() {
      const referenceId = this.application?.detailViewModel.referenceId;

      if (referenceId) {
        const currentTab = this.tabs?.find((t) => t.isTabActive);
        const tabs: TabResponse = await $api.applications.getTabTagsAsync(
          referenceId
        );
        if (tabs.success && currentTab) {
          this.tabs = tabs.results.tabs;
          this.tags = tabs.results.tags;
          this.updateActiveTab(currentTab.identifier);
        }
      }
    },
    setApplicationMetadata(metadata: {
      referenceId: string;
      applicationId: number;
      lenderId: number;
      productTypeId: number;
      programTypeId: number;
    }) {
      this.applicationMetadata = metadata;
    },
    updateActiveApplicant(applicantId: number | null) {
      this.applicants.forEach((applicant) => {
        applicant.selected = false;
      });

      this.applicantsTab.forEach((applicant) => {
        applicant.selected = false;
      });

      const applicant = this.applicants.find((a) => a.id == applicantId);

      const applicantTab = this.applicantsTab.find((a) => a.id == applicantId);

      if (applicant) {
        applicant.selected = true;
      }

      if (applicantTab) {
        applicantTab.selected = true;
      }
    },
    setApplicantLoading(applicantId: number | null, flag: boolean) {
      this.applicants.forEach((student) => {
        student.loading = false;
      });

      this.applicantsTab.forEach((student) => {
        student.loading = false;
      });

      const applicant = this.applicants.find((a) => a.id == applicantId);

      const applicantTab = this.applicantsTab.find((a) => a.id == applicantId);

      if (applicant) {
        applicant.loading = flag;
      }

      if (applicantTab) {
        applicantTab.loading = flag;
      }
    },
    setStudentLoading(applicantId: number | null, flag: boolean) {
      this.students.forEach((student) => {
        student.loading = false;
      });

      this.studentsTab.forEach((student) => {
        student.loading = false;
      });

      const student = this.students.find((a) => a.id == applicantId);

      const studentTab = this.studentsTab.find((a) => a.id == applicantId);

      if (student) {
        student.loading = flag;
      }

      if (studentTab) {
        studentTab.loading = flag;
      }
    },
    updateActiveStudent(applicantId: number | null) {
      this.students.forEach((student) => {
        student.selected = false;
      });

      this.studentsTab.forEach((student) => {
        student.selected = false;
      });

      let student = this.students.find((a) => a.id == applicantId);

      let studentTab = this.studentsTab.find((a) => a.id == applicantId);

      if (student) {
        student.selected = true;
      }

      if (studentTab) {
        studentTab.selected = true;
      }

      if (
        this.application?.detailViewModel?.hasStudent &&
        this.application?.detailViewModel?.student
      ) {
        student = {
          ...this.application.detailViewModel.student,
          selected: true,
        };
        studentTab = {
          ...this.application.tabApplicationViewModel.student,
          selected: true,
        };
      }
    },
    refreshActiveApplicant(applicantId: number) {
      console.log("refreshActiveApplicant");

      // refresh the data
      this.applicantsTab.forEach((applicant) => {
        if (applicant.id !== applicantId) {
          applicant.selected = false;
        }
      });

      let applicant = this.applicants.find((a) => a.id == applicantId);

      let applicantTab = this.applicantsTab.find((a) => a.id == applicantId);

      if (applicant) {
        applicant.selected = true;
      }

      if (applicantTab) {
        applicantTab.selected = true;
      }
      // determine which applicant is active
      if (
        this.application?.detailViewModel?.hasPrimaryApplicant &&
        this.application?.detailViewModel?.primaryApplicant?.id === applicantId
      ) {
        applicant = {
          ...this.application.detailViewModel.primaryApplicant,
          selected: true,
        };

        applicantTab = {
          ...this.application.tabApplicationViewModel.primaryApplicant,
          selected: true,
        };
      }

      if (
        this.application?.detailViewModel?.hasCoApplicant &&
        this.application?.detailViewModel?.coApplicant?.id === applicantId
      ) {
        applicant = {
          ...this.application.detailViewModel.coApplicant,
          selected: true,
        };

        applicantTab = {
          ...this.application.tabApplicationViewModel.coApplicant,
          selected: true,
        };
      }
    },
    refreshActiveStudent(applicantId: number) {
      console.log("refreshActiveStudent", applicantId);

      this.applicants.forEach((a: ApplicantVM) => (a.selected = false));
      this.applicantsTab.forEach((a: ApplicantFormVM) => (a.selected = false));

      if (
        this.application?.detailViewModel?.hasStudent &&
        this.application?.detailViewModel?.student
      ) {
        this.application.detailViewModel.student.selected = true;
        this.application.tabApplicationViewModel.student.selected = true;

        const studentsIndex = this.students.findIndex(
          (studentVM) => studentVM.id === applicantId
        );

        if (studentsIndex !== -1) {
          this.students[studentsIndex].fullName =
            this.application.detailViewModel.student.fullName;
        }

        const studentTabIndex = this.studentsTab.findIndex(
          (studentVM) => studentVM.id === applicantId
        );

        if (studentsIndex !== -1) {
          this.studentsTab[studentTabIndex] =
            this.application.tabApplicationViewModel.student;
        }
      }
    },
    addAuthorizedThirdPartyForm() {
      const newAuthorizedThirdPartyForm: AuthorizedThirdPartyVM = {
        ...this.authorizedThirdPartyForm,
      };

      const authorizedPerson =
        this.application?.tabApplicationViewModel.authorizedThirdParty.find(
          (p) => p.id == 0
        );

      if (authorizedPerson) {
        authorizedPerson.selected = true;
      } else {
        this.application?.tabApplicationViewModel.authorizedThirdParty.push(
          newAuthorizedThirdPartyForm
        );
      }
    },
    discardNewAuthorizedThirdPartyChanges() {
      this.application?.tabApplicationViewModel.authorizedThirdParty.pop();
      this.addAuthorizedThirdPartyForm();
    },
    updateAuthorizedParties(personId: number) {
      this.application?.tabApplicationViewModel.authorizedThirdParty.forEach(
        (person) => {
          person.selected = false;
        }
      );

      const authorizedPerson =
        this.application?.tabApplicationViewModel.authorizedThirdParty.find(
          (p) => p.id == personId
        );

      if (authorizedPerson) {
        authorizedPerson.selected = true;
      }
    },
    updateActiveTab(identifier: string) {
      this.tabs?.forEach((element) => {
        element.isTabActive = false;
      });

      const tab = this.tabs?.find((t) => t.identifier == identifier);
      if (tab) {
        tab.isTabActive = true;
      }
    },
    updateTabStatus(tabIdentifier: string, status: string) {
      const tabToUpdate = this.tabs?.find(
        (t: TabVM) => t.identifier === tabIdentifier
      );

      if (tabToUpdate) {
        tabToUpdate.tabStatus = status;
      }
    },
    decodeApplicantFields(applicant: ApplicantVM) {
      if (applicant && applicant.id > 0) {
        applicant.addressDisplay = decodeHTMLEncodedStr(
          applicant.addressDisplay
        );
        applicant.fullName = decodeHTMLEncodedStr(applicant.fullName);
      }
      return applicant;
    },
    decodeApplicantFormFields(applicant: ApplicantFormVM) {
      if (applicant && applicant.id > 0) {
        applicant.fullName = decodeHTMLEncodedStr(applicant.fullName);
        applicant.firstName = decodeHTMLEncodedStr(applicant.firstName);
        applicant.middleName = decodeHTMLEncodedStr(applicant.middleName);
        applicant.lastName = decodeHTMLEncodedStr(applicant.lastName);
        applicant.address.address1 = decodeHTMLEncodedStr(
          applicant.address.address1
        );
        applicant.address.address2 = decodeHTMLEncodedStr(
          applicant.address.address2
        );
        applicant.address.city = decodeHTMLEncodedStr(applicant.address.city);
      }
      return applicant;
    },
    selectAllStudents(flag: boolean) {
      this.students.forEach((student) => {
        student.selected = flag;
      });

      this.studentsTab.forEach((student) => {
        student.selected = flag;
      });
    },
    selectAllApplicants(flag: boolean) {
      this.applicants.forEach((applicant) => {
        applicant.selected = flag;
      });

      this.applicantsTab.forEach((applicant) => {
        applicant.selected = flag;
      });
    },
    async getApplicationComments(referenceId: string) {
      try {
        if (this.applicationId == 0) {
          const application = await $api.applications.getSlimApplication(
            referenceId
          );

          if (application != undefined) {
            this.fallBackApplicationId = application.id;
          }
        }

        const response = await $api.applications.getApplicationComments(
          this.applicationId
        );
        if (response) {
          this.applicationComments = response;
        }
      } catch (error) {
        console.log(error);
      }
    },

    async getApplicationSummary(referenceId: string) {
      try {
        var response = await axios.get(
          `/api/applications/${referenceId}/summary`
        );

        if (
          response.data !== undefined &&
          response.data.results !== undefined
        ) {
          this.applicationSummary = response.data.results;
        }
      } catch (error) {
        console.error(error);
      }
    },

    async getApplicationRoute(referenceId: string) {
      if (this.applicationMetadata?.referenceId === referenceId) {
        return this.applicationMetadata.productTypeId;
      }

      try {
        const response = await axios.get(
          `/api/applications/${referenceId}/slim`
        );

        if (response !== undefined && response.data !== undefined) {
          this.applicationMetadata.productTypeId = response.data.productTypeId;
        }
      } catch (error) {
        console.error(error);
      }
    },

    async getApplication(referenceId: string) {
      try {
        const response = await axios.get(
          `/api/applications/${referenceId}/detail`
        );

        if (
          response.data !== undefined &&
          response.data.results !== undefined
        ) {
          this.application = response.data.results;
          if (this.application?.detailViewModel) {
            this.applicationMetadata.productTypeId =
              this.application.detailViewModel.productTypeId;

            this.setupApplicants();
          }
        }
      } catch (error) {
        console.error(error);
      }
    },

    setupApplicants() {
      this.applicants = [];
      this.applicantsTab = [];
      this.students = [];
      this.studentsTab = [];

      if (this.application?.detailViewModel.hasPrimaryApplicant) {
        this.application.detailViewModel.primaryApplicant =
          this.decodeApplicantFields(
            this.application.detailViewModel.primaryApplicant
          );

        this.application.tabApplicationViewModel.primaryApplicant =
          this.decodeApplicantFormFields(
            this.application.tabApplicationViewModel.primaryApplicant
          );

        this.application.detailViewModel.primaryApplicant.selected = true;
        this.application.tabApplicationViewModel.primaryApplicant.selected =
          true;
        this.applicants.push(this.application.detailViewModel.primaryApplicant);
        this.applicantsTab.push(
          this.application.tabApplicationViewModel.primaryApplicant
        );
      }
      if (this.application?.detailViewModel.hasCoApplicant) {
        this.application.detailViewModel.coApplicant =
          this.decodeApplicantFields(
            this.application.detailViewModel.coApplicant
          );

        this.application.tabApplicationViewModel.coApplicant =
          this.decodeApplicantFormFields(
            this.application.tabApplicationViewModel.coApplicant
          );

        this.application.detailViewModel.coApplicant.selected =
          !this.application?.detailViewModel.hasPrimaryApplicant;

        this.application.tabApplicationViewModel.coApplicant.selected =
          !this.application?.detailViewModel.hasPrimaryApplicant;

        this.applicants.push(this.application?.detailViewModel.coApplicant);
        this.applicantsTab.push(
          this.application.tabApplicationViewModel.coApplicant
        );
      }

      if (
        this.application?.detailViewModel?.hasStudent &&
        this.application?.detailViewModel?.student
      ) {
        this.application.detailViewModel.student.selected = false;
        this.application.tabApplicationViewModel.student.selected = false;

        this.students.push(this.application.detailViewModel.student);
        this.studentsTab.push(this.application.tabApplicationViewModel.student);
      }
    },

    async getPreviousAddresses(applicantId: number) {
      try {
        const fiveMinutes = 300000;
        const cacheApplicantAddress = functions.checkLocalStorage(
          `cache.${applicantId}Address`,
          fiveMinutes
        );
        if (cacheApplicantAddress) {
          this.applicantPreviousAddresses[applicantId] =
            cacheApplicantAddress.data;
        } else {
          const response = await axios.get(
            `/api/applicants/${applicantId}/previousAddresses`
          );
          if (response) {
            response.data.forEach((a: ApplicantAddress) => {
              a.address1 = decodeHTMLEncodedStr(a.address1);
              a.address2 = decodeHTMLEncodedStr(a.address2);
              a.city = decodeHTMLEncodedStr(a.city);
            });

            localStorage.setItem(
              `cache.${applicantId}Address`,
              JSON.stringify({ timestamp: Date.now(), data: response.data })
            );

            this.applicantPreviousAddresses[applicantId] = response.data;
          }
        }
      } catch (error) {
        console.error(error);
      }
    },

    updateSchool(schoolData: SchoolUpdate) {
      if (this.application?.tabApplicationViewModel) {
        this.application.tabApplicationViewModel.school = {
          ...this.application.tabApplicationViewModel.school,
          ...schoolData,
        };
      } else {
        console.error("Application tab is not loaded");
      }
    },
  },
});
